/**
 * QA Evidence Modal
 * Story 288: View QA evidence for completed stories
 *
 * Displays QA checklist content and evidence provided during story completion.
 */

import { useEffect } from 'react';
import { MarkdownPreview } from '../editor/MarkdownPreview';

/** Evidence dimension labels for display */
const EVIDENCE_LABELS: Record<string, string> = {
  tech_design_coverage: 'Tech Design Coverage',
  prd_test_strategy: 'PRD Test Strategy',
  design_test_strategy: 'Design Test Strategy',
  test_type_appropriateness: 'Test Type Appropriateness',
  test_equivalence_coverage: 'Test Equivalence Coverage',
};

/** Order of evidence dimensions to display */
const EVIDENCE_KEYS = [
  'tech_design_coverage',
  'prd_test_strategy',
  'design_test_strategy',
  'test_type_appropriateness',
  'test_equivalence_coverage',
];

interface QAEvidenceModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Key-value pairs from qa_evidence */
  evidence: Record<string, string>;
}

export function QAEvidenceModal({
  isOpen,
  onClose,
  evidence,
}: QAEvidenceModalProps) {
  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--z-modal-backdrop, 200)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-base, #fff)',
          borderRadius: 'var(--radius-lg, 8px)',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--text-primary, #1e293b)',
              }}
            >
              QA Evidence
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: '13px',
                color: 'var(--text-muted, #94a3b8)',
              }}
            >
              Quality review evidence for story completion
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              fontSize: '18px',
              color: 'var(--text-muted, #94a3b8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              lineHeight: 1,
            }}
            title="Close"
            aria-label="Close QA evidence modal"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
          }}
        >
          {/* Evidence Dimensions Section */}
          <div>
            <h3
              style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-secondary, #64748b)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Evidence Provided
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {EVIDENCE_KEYS.map((key) => {
                const label = EVIDENCE_LABELS[key];
                const value = evidence[key];
                const hasValue = value && value.trim().length > 0;

                return (
                  <div
                    key={key}
                    style={{
                      backgroundColor: 'var(--bg-subtle, #f8fafc)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      border: '1px solid var(--border-light, #e2e8f0)',
                    }}
                  >
                    <h4
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'var(--text-primary, #1e293b)',
                      }}
                    >
                      {label}
                    </h4>
                    {hasValue ? (
                      <MarkdownPreview content={value} />
                    ) : (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          color: 'var(--text-muted, #94a3b8)',
                          fontStyle: 'italic',
                        }}
                      >
                        Not provided
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              color: 'var(--text-secondary, #64748b)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-default, #e2e8f0)',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
